import React, { FC, useEffect } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import ConfirmationModalActions from '../ConfirmationModal/ConfirmationModalActions';
import ConfirmationModalContent from '../ConfirmationModal/ConfirmationModalContent';
import { showNotification } from '../NotificationBanner/showNotification';
import { adminLeaveTerritoryMutation } from '../Territory/mutations/adminLeaveTerritoryMutation';
import { disclaimTerritoryMembershipMutation } from '../Territory/mutations/disclaimTerritoryMembershipMutation';
import { TUserTerritoryPermissions } from '../Territory/types';
import { getTimepointsForDisclaimingQuery } from '../Territory/queries/getTimepointsForDisclaiming';
import { formatNumber } from '../../../utils/formatNumber';
import { useTerritoryDetailsModalContext } from '../TerritoryDetails/context/TerritoryDetailsModalContextProvider';

type TLeaveTerritoryModalComponentProps = {
  territoryId: string;
  territoryName: string;
  refreshUserData: () => void;
  userTerritoryPermissions: TUserTerritoryPermissions;
};

export const LeaveTerritoryModalComponent: FC<TLeaveTerritoryModalComponentProps> = ({
  territoryId,
  territoryName,
  refreshUserData,
  userTerritoryPermissions,
}) => {
  const { isOpen, closeModal, ModalComponent } = useTerritoryDetailsModalContext();
  const { isTerritoryMember, isTerritoryAdmin } = userTerritoryPermissions;

  const [getTimepointsForDisclaiming, { data: disclaimingData }] = useLazyQuery(getTimepointsForDisclaimingQuery);
  const timepointsForDisclaiming = formatNumber(Number(disclaimingData?.getTimepointsForDisclaiming?.timePoints) || 0);

  const [leaveTerritory, { loading: isLeaveTerritoryLoading }] = useMutation(
    isTerritoryAdmin ? adminLeaveTerritoryMutation : disclaimTerritoryMembershipMutation,
    {
      variables: {
        territoryId,
      },
      onError: () => {
        showNotification({
          message: 'An error has occurred, please refresh the page and try again.',
          type: 'error',
          title: 'Error',
        });
      },
    },
  );

  const handleConfirm = (closeModal: () => void) => async () => {
    const { errors } = await leaveTerritory();

    if (!errors) {
      closeModal();
      showNotification({
        message: (
          <>
            <div>You have left this Territory. It was good having you!</div>
            {isTerritoryMember && <div>We returned your time points deposit.</div>}
          </>
        ),
        type: 'success',
        title: '',
      });
      refreshUserData();
    }
  };

  const handleConfirmButtonClick = () => {
    handleConfirm(closeModal)();
  };

  useEffect(() => {
    if (territoryId && isOpen && isTerritoryMember) {
      getTimepointsForDisclaiming({
        variables: {
          territoryId,
        },
      });
    }
  }, [territoryId, isOpen, isTerritoryMember]);

  return (
    <ModalComponent
      title={`Are you sure you want to leave t/${territoryName}?`}
      footer={
        <ConfirmationModalActions
          handleCancel={closeModal}
          handleConfirm={handleConfirmButtonClick}
          isLoading={isLeaveTerritoryLoading}
          confirmationButtonText="Leave Territory"
        />
      }
    >
      <ConfirmationModalContent
        body={
          isTerritoryAdmin
            ? 'You will lose Admin permissions, but still able to rejoin the Territory as a member.'
            : `You'll still be able to read, but will lose the ability to create new posts and engage. Your ${timepointsForDisclaiming} t.p. deposit will be returned.`
        }
      />
    </ModalComponent>
  );
};
